import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Avatar,
  Grid,
  Divider,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';

function ProfilePage() {
  const { user } = useAuth();

  const userName = user?.nombre || user?.usu_nombre || 'Usuario';
  const userLastName = user?.apellido || user?.usu_apellido || '';
  const userEmail = user?.email || user?.usu_email || 'No disponible';
  const userRol = user?.usu_rol || user?.rol || 'Usuario';
  const userInitial = userName.charAt(0).toUpperCase();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#001F3F',
            mb: 1,
            fontSize: { xs: '1.75rem', sm: '2rem' }
          }}
        >
          Mi Perfil
        </Typography>
        <Typography variant="body1" sx={{ color: '#666' }}>
          Información de tu cuenta y configuración
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Tarjeta Principal de Perfil */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
              border: '1px solid rgba(0, 31, 63, 0.08)',
              textAlign: 'center',
              height: '100%',
            }}
          >
            <Avatar
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                mb: 2,
                background: 'linear-gradient(135deg, #3A6B7E 0%, #001F3F 100%)',
                fontSize: '3rem',
                fontWeight: 600,
                boxShadow: '0 4px 20px rgba(58, 107, 126, 0.3)',
              }}
            >
              {userInitial}
            </Avatar>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#001F3F',
                mb: 1,
              }}
            >
              {userName} {userLastName}
            </Typography>

            <Chip
              label={userRol}
              sx={{
                backgroundColor: 'rgba(58, 107, 126, 0.1)',
                color: '#3A6B7E',
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 1,
                mb: 3,
              }}
            />

            <Divider sx={{ my: 3 }} />

            <Box sx={{ textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ color: '#3A6B7E', mr: 1.5 }} />
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {userEmail}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Información Detallada */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* Información Personal */}
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(0, 31, 63, 0.08)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <PersonIcon sx={{ color: '#3A6B7E', mr: 1.5, fontSize: 28 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#001F3F',
                      }}
                    >
                      Información Personal
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#999',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        Nombre
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#001F3F',
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {userName}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#999',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        Apellido
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#001F3F',
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {userLastName || 'No especificado'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#999',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        Correo Electrónico
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#001F3F',
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {userEmail}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#999',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        Rol
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={userRol}
                          sx={{
                            backgroundColor: 'rgba(58, 107, 126, 0.1)',
                            color: '#3A6B7E',
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Información de la Empresa */}
            {user?.emp_codigo && (
              <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(0, 31, 63, 0.08)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <BusinessIcon sx={{ color: '#3A6B7E', mr: 1.5, fontSize: 28 }} />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#001F3F',
                        }}
                      >
                        Información de la Empresa
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666',
                      }}
                    >
                      Código de Empresa: {user.emp_codigo}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Información del Sistema */}
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(0, 31, 63, 0.08)',
                  background: 'linear-gradient(135deg, rgba(0, 31, 63, 0.02) 0%, rgba(58, 107, 126, 0.02) 100%)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BadgeIcon sx={{ color: '#3A6B7E', mr: 1.5, fontSize: 28 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#001F3F',
                      }}
                    >
                      Información del Sistema
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#666',
                      mb: 1,
                    }}
                  >
                    ID de Usuario: {user?.usu_codigo || user?.id || 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
}

export default ProfilePage;